import React, { useMemo } from "react";
import { useLocation, Outlet } from "react-router-dom";
import { VueAppBridge } from "./VueAppBridge";
import { isReactNativePath } from "../router/reactNativePaths";
import type { NavView } from "../../store";

/**
 * React Router 경로를 Vue navView 값으로 매핑.
 * VueBridgeLayout 은 단일 VueAppBridge 인스턴스를 렌더링하며,
 * Vue 브리지 라우트가 변경되어도 계속 마운트된 상태를 유지하여
 * Pinia 스토어 상태(solver 상태, 결과 등)를 보존한다.
 *
 * React 네이티브 페이지(`/documents/*`, `/game`, `/settings`)는
 * `reactNativePaths.ts` 의 SSOT 에서 유도되어 `deriveNavView` 에서 일괄 "home" 으로 매핑된다.
 * 이 테이블에는 Vue 브리지 라우트만 명시한다.
 */
const pathToNavView: Record<string, NavView> = {
  "/": "home",
  "/solver": "solver",
  "/results": "results",
  "/history": "solve-history",
  "/preflop-solutions": "preflop-solutions",
  "/preflop-trace": "preflop-trace",
  // React Router가 canonical URL로 바꾸는 한 프레임 동안에도 같은 Vue 화면을 유지한다.
  "/preflop-trace-2": "preflop-trace",
  "/ai-setup": "ai-setup",
};

function deriveNavView(pathname: string): NavView {
  // /results/c/:configHash 패턴
  if (pathname.startsWith("/results/")) {
    return "results";
  }
  // React 네이티브 페이지 — Pinia 보존을 위해 VueBridgeLayout 마운트는 유지하되 navView 는 `home`
  if (isReactNativePath(pathname)) {
    return "home";
  }
  return pathToNavView[pathname] || "home";
}

/**
 * 모든 Vue 브리지 라우트를 단일 영속 VueAppBridge 인스턴스로 감싸는 레이아웃 컴포넌트.
 * Solver, Results, History, Preflop 페이지 간 네비게이션 시
 * Pinia 스토어(및 모든 solver 상태)가 보존된다.
 */
export function VueBridgeLayout() {
  const location = useLocation();
  const navView = useMemo(
    () => deriveNavView(location.pathname),
    [location.pathname]
  );
  // React-native 경로에서는 Vue 앱을 unmount 하지 않고(=Pinia 보존) 시각 트리만 숨긴다.
  const hideVueTree = useMemo(
    () => isReactNativePath(location.pathname),
    [location.pathname]
  );
  return (
    <>
      <VueAppBridge
        navView={navView}
        routePath={
          location.pathname === "/image-relay/connect"
            ? location.pathname + location.search
            : location.pathname
        }
        hidden={hideVueTree}
      />
      <Outlet />
    </>
  );
}
